import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { Icon, IconComponent } from '@examdojo/core/icon';
import { ChecklistItem, ChecklistItemId, ChecklistItemType } from '@examdojo/question';
import { TextareaInputComponent } from '@examdojo/ui/input';

@Component({
  selector: 'dojo-consensus-checklist-item',
  template: `
    @if (!!valueControl) {
      <div class="checklist-item">
        @if (item.type === ChecklistItemType.YesNo) {
          <div class="question" [class]="item.explanation ? 'mb-0' : 'mb-2'">{{ item.display_name }}</div>
          @if (item.explanation) {
            <a
              class="mb-2 mt-1 flex items-center text-sm text-neutral-500 hover:text-sky-500"
              target="_blank"
              [href]="item.explanation"
            >
              See explanation
              <dojo-icon class="ml-1" size="12" [icon]="'arrow-right'" />
            </a>
          }

          <mat-button-toggle-group
            [formControl]="valueControl"
            hideSingleSelectionIndicator
            class="custom-toggle-group size-s"
            (change)="onValueChange()"
          >
            <mat-button-toggle [value]="true" class="flex-1">
              <div class="flex items-center gap-2">
                <dojo-icon [icon]="getToggleIcon(true)" [iconClass]="getToggleIconClass(true)" [size]="14" />
                Yes
              </div>
            </mat-button-toggle>
            <mat-button-toggle [value]="false" class="flex-1">
              <div class="flex items-center gap-2">
                <dojo-icon [icon]="getToggleIcon(false)" [iconClass]="getToggleIconClass(false)" [size]="14" />
                No
              </div>
            </mat-button-toggle>
          </mat-button-toggle-group>
        }

        @if (shouldShowExtraTextField()) {
          <dojo-textarea-input
            [placeholder]="'Describe why you are reporting this'"
            [resizable]="true"
            [autoResize]="true"
            [formCtrl]="commentControl!"
            class="mt-2 w-full"
          />
        }

        @if (shouldShowFixedToggle()) {
          <div class="mt-3">
            <div class="question mb-2 mt-6">Can you fix this issue?</div>
            <mat-button-toggle-group
              [formControl]="fixedControl!"
              hideSingleSelectionIndicator
              class="custom-toggle-group size-s"
              (change)="onValueChange()"
            >
              <mat-button-toggle [value]="true" class="flex-1">
                <div class="flex items-center gap-2">
                  <dojo-icon
                    [icon]="'check'"
                    [iconClass]="fixedControl?.value === true ? 'text-green-500' : 'text-neutral-300'"
                    [size]="14"
                  />
                  I fixed it
                </div>
              </mat-button-toggle>
              <mat-button-toggle [value]="false" class="flex-1">
                <div class="flex items-center gap-2">
                  <dojo-icon
                    [icon]="'xmark'"
                    [iconClass]="fixedControl?.value === false ? 'text-red-500' : 'text-neutral-300'"
                    [size]="14"
                  />
                  I cannot fix it
                </div>
              </mat-button-toggle>
            </mat-button-toggle-group>
          </div>
        }
      </div>
    }
  `,
  styles: [
    `
      .question {
        @apply text-md text-primary font-normal;
      }
    `,
  ],
  imports: [
    TextareaInputComponent,
    ReactiveFormsModule,
    MatRadioModule,
    MatButtonToggleModule,
    TextareaInputComponent,
    IconComponent,
  ],
})
export class ConsensusChecklistItemComponent {
  @Input() item!: ChecklistItem;
  @Input() formGroupName!: ChecklistItemId;
  @Input() parentFormGroup!: FormGroup;
  @Input() calculatorAllowed = false;
  @Input() editingAllowed = true;

  @Input() shouldShowExtraTextFieldFn!: (item: ChecklistItem) => boolean;
  @Input() isItemVisibleFn!: (item: ChecklistItem) => boolean;
  @Input() shouldShowFixedToggleFn!: (item: ChecklistItem) => boolean;
  @Input() checkPassConditionFn!: (item: ChecklistItem, value: unknown) => boolean;

  @Output() valueChange = new EventEmitter<void>();
  @Output() showExplanation = new EventEmitter<string>();

  readonly ChecklistItemType = ChecklistItemType;

  onValueChange(): void {
    this.updateFormValidators();
    this.valueChange.emit();
  }

  get valueControl(): FormControl | null {
    return this.getFormControl();
  }

  get commentControl(): FormControl | null {
    const group = this.parentFormGroup.get(this.formGroupName) as FormGroup;
    if (!group) {
      return null;
    }

    // If comment control doesn't exist yet, create it
    if (!group.get('comment')) {
      group.addControl('comment', new FormControl(''));
    }

    return group.get('comment') as FormControl;
  }

  get fixedControl(): FormControl | null {
    const group = this.parentFormGroup.get(this.formGroupName) as FormGroup;
    if (!group) {
      return null;
    }

    // If fixed control doesn't exist yet, create it
    if (!group.get('fixed')) {
      group.addControl('fixed', new FormControl(null));
    }

    return group.get('fixed') as FormControl;
  }

  // Add this method to update validators based on conditions
  updateFormValidators(): void {
    if (!this.commentControl) {
      return;
    }

    if (this.shouldShowExtraTextField()) {
      this.commentControl.setValidators([Validators.required]);
    } else {
      this.commentControl.clearValidators();
    }

    this.commentControl.updateValueAndValidity();

    // Handle fixed control validators
    if (this.fixedControl) {
      if (this.shouldShowFixedToggle()) {
        this.fixedControl.setValidators([Validators.required]);
      } else {
        this.fixedControl.clearValidators();
        this.fixedControl.setValue(null);
      }
      this.fixedControl.updateValueAndValidity();
    }
  }

  getFormControl(): FormControl | null {
    const control = this.parentFormGroup.get(this.formGroupName);
    if (!control) {
      return null;
    }

    // If this is a FormGroup (as expected in the ConsensusFeedbackFormComponent),
    // return the 'value' control within it
    if (control instanceof FormGroup && control.get('value')) {
      return control.get('value') as FormControl;
    }

    // Fallback - return the control itself if it's a FormControl
    return control as FormControl;
  }

  shouldShowExtraTextField(): boolean {
    return this.shouldShowExtraTextFieldFn(this.item);
  }

  shouldShowFixedToggle(): boolean {
    // Don't show fixed toggle if editing is not allowed
    if (!this.editingAllowed) {
      return false;
    }

    return this.shouldShowFixedToggleFn(this.item);
  }

  getToggleIconClass(value: boolean): string {
    const currentValue = this.valueControl?.value;

    // If this option is not selected, show gray
    if (currentValue !== value) {
      return 'text-neutral-300';
    }

    // If this option is selected, show green if it passes the condition, red if it doesn't
    if (this.checkPassConditionFn(this.item, value)) {
      return 'text-green-500';
    } else {
      return 'text-red-500';
    }
  }

  getToggleIcon(value: boolean): Icon {
    // Show check icon for the option that passes the condition, xmark for the one that fails
    if (this.checkPassConditionFn(this.item, value)) {
      return 'check' satisfies Icon;
    } else {
      return 'xmark' satisfies Icon;
    }
  }
}
